import React, { useEffect, useMemo, useState } from "react";
import { aboutData } from "../data/aboutData";
import { contactData } from "../data/contactData";
import { educationData } from "../data/educationData";
import { experienceData, WorkExperience } from "../data/experienceData";
import { heroData } from "../data/heroData";
import { projectsData } from "../data/projectsData";
import { placeholderImages } from "../utils/placeholderImages";

const NAV = [
  { id: "work", label: "Work" },
  { id: "projects", label: "Projects" },
  { id: "skills", label: "Skills" },
  { id: "education", label: "Education" },
  { id: "contact", label: "Contact" },
];

const FEATURED_FULLTIME = [
  "rox-group",
  "tokyolife-pm",
  "tokyolife-lead",
  "tokyolife-forlife",
  "tokyolife-cms",
  "fanxipan",
  "ceti",
];

function stripMarkdown(text: string): string {
  return text.replace(/\*\*/g, "");
}

function groupByCompany(items: WorkExperience[]) {
  const groups: { company: string; period: string; roles: WorkExperience[] }[] =
    [];
  const map = new Map<string, number>();

  items.forEach((exp) => {
    const idx = map.get(exp.company);
    if (idx === undefined) {
      map.set(exp.company, groups.length);
      groups.push({ company: exp.company, period: exp.period, roles: [exp] });
    } else {
      groups[idx].roles.push(exp);
    }
  });

  return groups;
}

export const WebPortfolio: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const workGroups = useMemo(
    () =>
      groupByCompany(
        experienceData.experiences.filter((e) =>
          FEATURED_FULLTIME.includes(e.id)
        )
      ),
    []
  );

  const education = educationData.education[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ top, behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div className="web-portfolio min-h-screen text-ink">
      {/* Nav */}
      <header
        className={`web-nav fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "web-nav--solid" : ""
        }`}
      >
        <div className="web-container flex h-[4.25rem] items-center justify-between">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="font-display text-lg tracking-tight text-ink"
          >
            {heroData.name}
          </button>

          <nav className="hidden items-center gap-7 md:flex">
            {NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(item.id)}
                className="text-sm text-ink/65 transition-colors hover:text-ink"
              >
                {item.label}
              </button>
            ))}
            <a href={heroData.downloadCvUrl} className="web-btn web-btn--sm" download>
              Download PDF
            </a>
          </nav>

          <button
            type="button"
            className="md:hidden rounded-md border border-ink/10 px-3 py-1.5 text-sm"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            Menu
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-ink/8 bg-paper/95 px-5 py-4 backdrop-blur md:hidden">
            <div className="flex flex-col gap-3">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="text-left text-sm text-ink/80"
                >
                  {item.label}
                </button>
              ))}
              <a
                href={heroData.downloadCvUrl}
                className="web-btn web-btn--sm w-fit"
                download
              >
                Download PDF
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero — one composition */}
      <section className="web-hero relative overflow-hidden pt-[4.25rem]">
        <div className="web-hero-glow" aria-hidden />
        <div className="web-hero-grid" aria-hidden />

        <div className="web-container relative grid min-h-[calc(100vh-4.25rem)] items-center gap-10 py-14 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:py-10">
          <div className="web-reveal">
            <p className="font-display text-[clamp(2.75rem,7vw,4.75rem)] font-semibold leading-[0.95] tracking-tight text-ink">
              {heroData.name}
            </p>
            <h1 className="mt-5 max-w-xl text-xl font-medium leading-snug text-ink/85 sm:text-2xl">
              {heroData.title}
            </h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-ink/60">
              Building high-performance mobile & backend systems, leading teams,
              and shipping scalable products for startups and enterprises.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => scrollTo("work")}
                className="web-btn"
              >
                View experience
              </button>
              <a
                href={heroData.downloadCvUrl}
                className="web-btn web-btn--ghost"
                download
              >
                Download PDF
              </a>
              <a href="#/cv" className="web-link-quiet">
                Printable CV →
              </a>
            </div>

            <p className="mt-8 text-sm text-ink/45">{heroData.location}</p>
          </div>

          <div className="web-reveal web-reveal--delay relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="web-portrait">
              <img
                src={heroData.profileImage}
                alt={heroData.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = placeholderImages.profile;
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="border-y border-ink/8 bg-paper-soft">
        <div className="web-container py-12 lg:py-14">
          <p className="mx-auto max-w-3xl text-center text-lg leading-relaxed text-ink/70 sm:text-xl">
            {stripMarkdown(aboutData.intro)}
          </p>
        </div>
      </section>

      {/* Work */}
      <section id="work" className="web-container py-16 lg:py-24">
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="web-eyebrow">Experience</p>
            <h2 className="font-display text-3xl tracking-tight text-ink sm:text-4xl">
              Selected work
            </h2>
          </div>
          <a href="#/cv" className="web-link-quiet hidden sm:inline">
            Full CV (PDF style) →
          </a>
        </div>

        <div className="space-y-0">
          {workGroups.map((group, gi) => (
            <article
              key={group.company}
              className="web-work-row grid gap-4 border-t border-ink/10 py-8 lg:grid-cols-[12rem_1fr] lg:gap-10"
              style={{ animationDelay: `${gi * 40}ms` }}
            >
              <div>
                <p className="text-sm tabular-nums text-ink/45">{group.period}</p>
              </div>
              <div>
                <h3 className="font-display text-xl text-ink">{group.company}</h3>
                {group.roles.map((role) => (
                  <div key={role.id} className="mt-4 first:mt-2">
                    <p className="text-[15px] font-medium text-accent">
                      {role.position}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-ink/55">
                      {role.description}
                    </p>
                    <ul className="mt-3 space-y-1.5">
                      {role.responsibilities.slice(0, 3).map((r) => (
                        <li
                          key={r}
                          className="flex gap-2 text-sm leading-relaxed text-ink/70"
                        >
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent/70" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                    {role.technologies && (
                      <p className="mt-3 text-xs tracking-wide text-ink/40">
                        {role.technologies.slice(0, 8).join(" · ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="bg-ink text-paper">
        <div className="web-container py-16 lg:py-24">
          <p className="web-eyebrow web-eyebrow--light">Projects</p>
          <h2 className="font-display text-3xl tracking-tight sm:text-4xl">
            Built & shipped
          </h2>
          <p className="mt-3 max-w-xl text-paper/55">
            {projectsData.description}
          </p>

          <div className="mt-10 divide-y divide-paper/10">
            {projectsData.projects.map((project, i) => (
              <a
                key={project.id}
                href={project.url === "#" ? undefined : project.url}
                target={project.url !== "#" ? "_blank" : undefined}
                rel={project.url !== "#" ? "noreferrer" : undefined}
                className={`web-project-row group flex flex-col gap-2 py-6 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8 ${
                  project.url === "#" ? "cursor-default" : ""
                }`}
                style={{ animationDelay: `${i * 50}ms` }}
                onClick={(e) => {
                  if (project.url === "#") e.preventDefault();
                }}
              >
                <div>
                  <div className="flex flex-wrap items-baseline gap-3">
                    <h3 className="font-display text-2xl tracking-tight transition-colors group-hover:text-accent-soft">
                      {project.title}
                    </h3>
                    <span className="text-xs uppercase tracking-wider text-paper/35">
                      {project.category}
                    </span>
                  </div>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-paper/55">
                    {project.description}
                  </p>
                  <p className="mt-2 text-xs text-paper/35">
                    {project.technologies.join(" · ")}
                  </p>
                </div>
                {project.url !== "#" && (
                  <span className="shrink-0 text-sm text-paper/40 transition-colors group-hover:text-accent-soft">
                    Open ↗
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section id="skills" className="web-container py-16 lg:py-24">
        <p className="web-eyebrow">Capabilities</p>
        <h2 className="font-display text-3xl tracking-tight text-ink sm:text-4xl">
          Skills
        </h2>

        <div className="mt-10 grid gap-8 sm:grid-cols-2">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink/40">
              Front-end & Mobile
            </h3>
            <p className="mt-3 text-base leading-relaxed text-ink/75">
              {aboutData.frontendSkills.join(" · ")}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink/40">
              Backend & Data
            </h3>
            <p className="mt-3 text-base leading-relaxed text-ink/75">
              {aboutData.backendSkills.join(" · ")}
            </p>
          </div>
          <div className="sm:col-span-2">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink/40">
              Highlights
            </h3>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
              {aboutData.highlights.map((h) => (
                <span key={h} className="text-sm text-ink/65">
                  — {h}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Education */}
      {education && (
        <section id="education" className="border-t border-ink/8 bg-paper-soft">
          <div className="web-container py-16 lg:py-20">
            <p className="web-eyebrow">Education</p>
            <div className="mt-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-baseline">
              <div>
                <h2 className="font-display text-2xl tracking-tight text-ink sm:text-3xl">
                  {education.institution}
                </h2>
                <p className="mt-2 text-ink/65">{education.degree}</p>
                <p className="mt-2 max-w-2xl text-sm text-ink/50">
                  {education.description}
                </p>
              </div>
              <p className="text-sm tabular-nums text-ink/45">
                {education.period}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section id="contact" className="web-container py-16 lg:py-24">
        <p className="web-eyebrow">Contact</p>
        <h2 className="font-display text-3xl tracking-tight text-ink sm:text-4xl">
          Let&apos;s talk
        </h2>
        <p className="mt-3 max-w-md text-ink/55">{contactData.subtitle}</p>

        <div className="mt-10 flex flex-col gap-4 text-lg sm:flex-row sm:flex-wrap sm:gap-x-10 sm:gap-y-3">
          <a
            href={`mailto:${contactData.email}`}
            className="web-contact-link"
          >
            {contactData.email}
          </a>
          {contactData.phone && (
            <a href={`tel:${contactData.phone}`} className="web-contact-link">
              {contactData.phone}
            </a>
          )}
          {heroData.socialLinks.github && (
            <a
              href={heroData.socialLinks.github}
              target="_blank"
              rel="noreferrer"
              className="web-contact-link"
            >
              GitHub
            </a>
          )}
          {heroData.socialLinks.linkedin && (
            <a
              href={heroData.socialLinks.linkedin}
              target="_blank"
              rel="noreferrer"
              className="web-contact-link"
            >
              LinkedIn
            </a>
          )}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <a href={heroData.downloadCvUrl} className="web-btn" download>
            Download PDF CV
          </a>
          <a href="#/cv" className="web-btn web-btn--ghost">
            Open printable CV
          </a>
        </div>
      </section>

      <footer className="border-t border-ink/8">
        <div className="web-container flex flex-col gap-2 py-8 text-sm text-ink/40 sm:flex-row sm:items-center sm:justify-between">
          <span>
            © {new Date().getFullYear()} {heroData.name}
          </span>
          <span>{heroData.quote}</span>
        </div>
      </footer>
    </div>
  );
};
